'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

export default function SignUpLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const { signIn, user, loading: authLoading } = useAuth();
  const router = useRouter();
  const supabase = createClient();

  React.useEffect(() => {
    if (!authLoading && user) {
      router.replace('/dashboard');
    }
  }, [user, authLoading, router]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await signIn(email, password);

      // Check role — staff_pending users see the awaiting approval screen
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('role, account_status')
        .eq('id', data.user.id)
        .single();

      if (profile?.role === 'staff_pending') {
        window.location.href = '/awaiting-approval';
        return;
      }

      window.location.href = '/dashboard';
    } catch (err: any) {
      setError(err?.message || 'Invalid credentials. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-1 flex overflow-hidden">
      {/* Left panel: Brand */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col justify-between p-12 bg-surface-2 border-r border-white/[0.05] overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(232,160,32,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(232,160,32,0.06) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />
        <div
          className="absolute bottom-0 left-0 w-full h-1/2 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 30% 80%, rgba(232,160,32,0.08) 0%, transparent 60%)' }}
        />
        <div className="beam-border-v" />
        <div className="beam-border-h" />

        <div className="flex items-center gap-3 relative z-10">
          <AppLogo size={36} />
          <span className="font-manrope font-bold text-lg text-white">DealAnalyzer</span>
        </div>

        <div className="relative z-10">
          <h1
            className="font-manrope font-bold text-white leading-[0.88] tracking-tighter mb-6"
            style={{ fontSize: 'clamp(3rem, 5vw, 5rem)' }}
          >
            <span className="text-light-scan">INTERNAL</span>
            <br />
            <span className="text-neutral-600">STAFF</span>
            <br />
            <span className="text-light-scan">PORTAL</span>
          </h1>
          <p className="text-neutral-500 text-base font-light leading-relaxed max-w-sm">
            This tool is for authorized Ram Crest Enterprises staff only. Contact your administrator if you need access.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-4">
            {[
              { val: '16', label: 'Analysis steps' },
              { val: '94%', label: 'ARV accuracy' },
              { val: 'Bulk', label: 'Lead upload' },
              { val: 'Auto', label: 'CRM routing' },
            ].map((s) => (
              <div key={s.label} className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                <div className="text-xl font-manrope font-bold text-primary">{s.val}</div>
                <div className="text-[11px] font-jetbrains text-neutral-600 uppercase tracking-wider mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 p-4 rounded-xl bg-white/[0.02] border border-white/[0.05]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary shrink-0">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <p className="text-xs text-neutral-400 leading-relaxed">
              Restricted access — authorized personnel only. All activity is logged.
            </p>
          </div>
        </div>
      </div>

      {/* Right panel: Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12 relative">
        <div className="absolute top-6 left-6 flex items-center gap-2 lg:hidden">
          <AppLogo size={28} />
          <span className="font-manrope font-bold text-sm text-white">DealAnalyzer</span>
        </div>

        <div className="w-full max-w-md">
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div>
              <h2 className="text-2xl font-manrope font-bold text-white mb-1">Staff Sign In</h2>
              <p className="text-sm text-neutral-500">Enter your credentials to access the internal tool.</p>
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-jetbrains text-neutral-500 uppercase tracking-wider">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@ramcrest.com"
                className="input-dark w-full px-4 py-3 rounded-lg text-sm"
                required
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-jetbrains text-neutral-500 uppercase tracking-wider">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-dark w-full px-4 py-3 pr-12 rounded-lg text-sm"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-200 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-primary text-black font-bold text-sm rounded-lg hover:bg-primary-light transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </button>

            <p className="text-center text-xs text-neutral-600">
              Need access?{' '}
              <a href="/staff-register" className="text-neutral-400 hover:text-white transition-colors underline underline-offset-2">
                Request staff access
              </a>
            </p>

            <div className="pt-2 border-t border-white/[0.06]">
              <a
                href="/admin-login"
                className="w-full py-3 flex items-center justify-center gap-2 rounded-lg border border-primary/30 bg-primary/5 text-primary text-sm font-semibold hover:bg-primary/10 hover:border-primary/60 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Admin / Owner Login
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}